import { FormikValues } from "formik";
import {
  Field,
  SecuritiesFormikValues,
  Step,
  StepSixSecurities,
} from "../../types";
import { validationsForLanguage } from "../../validations";
import FeedbackCard from "../../components/FeedbackCard/FeedbackCard";
import { Alert, InputSelect } from "@almafintech/react-components";
import {
  onUpdateInitialValidationData,
  onUpdateLastCompletedSubStep,
  onUpdateSecuritiesData,
  onUpdateSubStep,
  onUpdateStep,
  onUpdateReachedStep,
  onUpdateCompletedStep,
} from "../../reducers/securitiesReducer";
import TokenStep from "../../components/SecuritiesTokenStep/SecuritiesTokenStep";
import {
  descriptions,
  labels,
  placeholders,
  primaryButtons,
  secondaryButtons,
  substepTitles,
  title,
} from "../../i18n/securities/prevStep";
import {
  accountHoldersItems,
  languageItems,
} from "../../i18n/securities/options";
import personalAccountIcon from "../../assets/images/icons/ui/body.svg";
import { RootState } from "../../app/store";
import { holder } from "../../i18n/securities/preconfirmationStep";
import { authEmail, deleteIndividualDraft } from "../../connectors/connectors";
import { updateSignatureRequestId } from "../../reducers/authReducer";
import { getCompanyData } from "../../utils";
import * as Sentry from "@sentry/react";
import addUserIcon from "../../assets/images/icons/ui/ui-user-plus-icon.svg";
import { texts } from "../../i18n/securities/stepFive";

const PrevStep = ({
  language,
  accountHolders,
  currentHolder,
}: {
  language: "es" | "en";
  accountHolders: number;
  currentHolder: number;
}): Step => {
  const validations = validationsForLanguage(language);

  const showHoldersMarriedStep = accountHolders === 2 && currentHolder === 1;

  const holdersMarriedStep: Step = {
    step: 4,
    name: "holdersMarried",
    hideLayoutButtons: true,
    component: (dispatch) => {
      return (
        <FeedbackCard
          title={`${title[language]} (${holder[language]} ${currentHolder})`}
          type="info"
          buttonText={primaryButtons[language].holdersMarried}
          secondaryButtonText={secondaryButtons[language].holdersMarried}
          description={substepTitles[language].holdersMarried}
          onButtonClick={() => {
            dispatch(onUpdateSecuritiesData({ holdersMarried: true }));
            dispatch(onUpdateSubStep(5));
            dispatch(onUpdateLastCompletedSubStep(4));
          }}
          onSeccondaryButtonClick={() => {
            dispatch(onUpdateSecuritiesData({ holdersMarried: false }));
            dispatch(onUpdateSubStep(5));
            dispatch(onUpdateLastCompletedSubStep(4));
          }}
        />
      );
    },
  };

  const mainHolderSteps: Step[] = [
    {
      step: 1,
      name: "Seleccionar idioma",
      hideLayoutButtons: true,
      component: (dispatch, state) => {
        return (
          <FeedbackCard
            title={title[language]}
            type="info"
            buttonText={primaryButtons[language].language}
            descriptionIsNode
            description={
              <div>
                <p>{descriptions[language].language}.</p>
                <InputSelect
                  style={{ marginTop: "1.5rem" }}
                  items={languageItems[language]}
                  value={state?.securities.language}
                  inputValue={state?.securities.language}
                  type="single"
                  placeholder={placeholders[language].language}
                  aria-label={placeholders[language].language}
                  onChange={(value) =>
                    dispatch(
                      onUpdateSecuritiesData({
                        language: value.toString() as "en" | "es",
                      })
                    )
                  }
                />
              </div>
            }
            onButtonClick={() => {
              dispatch(onUpdateSubStep(2));
              dispatch(onUpdateLastCompletedSubStep(1));
            }}
          />
        );
      },
    },
    {
      step: 2,
      name: "Ciudadanía estadounidense",
      hideLayoutButtons: true,
      component: (dispatch) => {
        return (
          <FeedbackCard
            title={`${title[language]} (${holder[language]} ${currentHolder})`}
            type="info"
            buttonText={primaryButtons[language].usCitizenship}
            description={descriptions[language].usCitizenship}
            onButtonClick={() => {
              dispatch(onUpdateSecuritiesData({ USCitizen: true }));
              dispatch(onUpdateSubStep(3));
              dispatch(onUpdateLastCompletedSubStep(2));
            }}
            secondaryButtonText={secondaryButtons[language].usCitizenship}
            onSeccondaryButtonClick={() => {
              dispatch(onUpdateSecuritiesData({ USCitizen: false }));
              dispatch(onUpdateSubStep(3));
              dispatch(onUpdateLastCompletedSubStep(2));
            }}
          />
        );
      },
    },
    {
      step: 3,
      name: "Titulares de cuenta",
      hideLayoutButtons: true,
      component: (dispatch, state) => {
        return (
          <FeedbackCard
            title={`${title[language]} (${holder[language]} ${currentHolder})`}
            type="info"
            buttonText={primaryButtons[language].accountHolders}
            descriptionIsNode
            description={
              <div>
                <p className="text-center">
                  {descriptions[language].accountHolders}
                </p>
                <InputSelect
                  style={{ marginTop: "1.5rem" }}
                  items={accountHoldersItems[language]}
                  value={state?.securities.accountHolders || ""}
                  inputValue={
                    state?.securities.accountHolders?.toString() || ""
                  }
                  type="single"
                  placeholder={placeholders[language].accountHolders}
                  aria-label={placeholders[language].accountHolders}
                  onChange={(value) =>
                    dispatch(
                      onUpdateSecuritiesData({
                        accountHolders: Number(value.toString()) || 1,
                      })
                    )
                  }
                />
                <Alert
                  variant="INFO"
                  className="mt-4"
                  children={
                    <>
                      <p>
                        <b>{texts[language].important}</b>
                      </p>
                      <p>{texts[language].beneficiariesInfo}</p>
                    </>
                  }
                />
              </div>
            }
            onButtonClick={() => {
              dispatch(onUpdateSecuritiesData({ holdersMarried: false }));
              dispatch(onUpdateSubStep(4));
              dispatch(onUpdateLastCompletedSubStep(3));
            }}
          />
        );
      },
    },
    ...(showHoldersMarriedStep ? [holdersMarriedStep] : []),
    {
      step: 4 + (showHoldersMarriedStep ? 1 : 0),
      name: "Cuenta personal",
      titleOfStep: `${substepTitles[language].personalAccount} ${
        currentHolder !== 1 ? `(${holder[language]} ${currentHolder})` : ""
      }`,
      icon: personalAccountIcon,
      hideStepper: true,
      fields: [
        {
          name: `taxId`,
          type: "text",
          label: labels[language].taxId,
          validation: validations.smallStringRequired,
          initialValue: "",
          placeholder: placeholders[language].taxId,
          conditionalBool: (_1, _2, state?: RootState) =>
            !state?.securities.USCitizen,
        },
        {
          name: `socialSecurityNumber`,
          type: "text",
          label: labels[language].socialSecurityNumber,
          validation: validations.ssnRequired,
          initialValue: "",
          placeholder: placeholders[language].socialSecurityNumber,
          conditionalBool: (_1, _2, state?: RootState) =>
            !!state?.securities.USCitizen,
        },
        ...((currentHolder === 1
          ? [
              {
                name: "email",
                type: "email",
                label: labels[language].mail,
                validation: validations.emailRequired,
                initialValue: "",
                placeholder: placeholders[language].mail,
              },
              {
                name: "referred",
                label: labels[language].referred,
                type: "text",
                initialValue: "",
                placeholder: placeholders[language].referred,
                validation: validations.smallStringRequired,
              },
            ]
          : []) as Field[]),
      ],
      onContinue: async (values: FormikValues, dispatch, state) => {
        try {
          if (!dispatch || !values)
            throw new Error("Error: dispatch not found");

          const { email, referred, taxId, socialSecurityNumber } = values;

          dispatch(
            onUpdateSecuritiesData({
              taxId: state?.securities.USCitizen ? socialSecurityNumber : taxId,
            })
          );

          if (currentHolder === 1) {
            const res = await authEmail({
              email,
              companyType: getCompanyData().companyName,
              es: state?.securities.language === "es",
            });

            dispatch(updateSignatureRequestId(res.data.signatureRequestId));
            dispatch(
              onUpdateInitialValidationData({
                email,
                referred,
                taxId: state?.securities.USCitizen
                  ? socialSecurityNumber
                  : taxId,
              })
            );
            dispatch(onUpdateSubStep(5));
            dispatch(onUpdateLastCompletedSubStep(4));
            if (email) {
              Sentry.setUser({ email });
            }
            return { newValuesToForm: { email } };
          }
        } catch (error) {
          console.error(error);
          Sentry.captureException(error);
          throw new Error("Error al validar el email");
        }
      },
    },
    {
      step: 5 + (showHoldersMarriedStep ? 1 : 0),
      name: "Validación de correo electrónico",
      component: (_, _1, formik) => {
        return <TokenStep formik={formik} language={language} />;
      },
    },
    {
      step: 6 + (showHoldersMarriedStep ? 1 : 0),
      name: "Confirmar onboarding incompleto",
      hideLayoutButtons: true,
      component: (dispatch, state, formik) => {
        return (
          <FeedbackCard
            title={substepTitles[language].unfinishedOnboarding}
            type="info"
            buttonText={primaryButtons[language].unfinishedOnboarding}
            description={descriptions[language].unfinishedOnboarding}
            onButtonClick={() => {
              const stepOne = state?.securities.sendedSteps.find(
                (step) => step["@type"] === "1_SECURITY"
              );
              const stepTwo = state?.securities.sendedSteps.find(
                (step) => step["@type"] === "2_SECURITY"
              );
              const stepThree = state?.securities.sendedSteps.find(
                (step) => step["@type"] === "3_SECURITY"
              );
              const stepFour = state?.securities.sendedSteps.find(
                (step) => step["@type"] === "4_SECURITY"
              );
              const stepFive = state?.securities.sendedSteps.find(
                (step) => step["@type"] === "5_SECURITY"
              );
              const stepSix = state?.securities.sendedSteps.find(
                (step) => step["@type"] === "6_SECURITY"
              );

              // filter sended steps
              const sendedSteps = [
                stepOne,
                stepTwo,
                stepThree,
                stepFour,
                stepFive,
                stepSix,
              ].filter(Boolean);

              if (sendedSteps.length < 5) {
                // update step and reached step to the last sended step
                dispatch(onUpdateStep(sendedSteps.length));
                dispatch(onUpdateReachedStep(sendedSteps.length));
                if (sendedSteps.length > 1) {
                  dispatch(onUpdateCompletedStep(sendedSteps.length - 1));
                }
                dispatch(onUpdateSubStep(1));
              } else if (stepFive || stepSix) {
                // sended step 5 and 6 are substeps 1 and 2 of step 5
                dispatch(onUpdateStep(5));
                dispatch(onUpdateReachedStep(5));
                dispatch(onUpdateCompletedStep(4));

                const stepSixFilled =
                  (stepSix as StepSixSecurities)?.beneficiariesInfo.length > 0;
                dispatch(onUpdateSubStep(stepSixFilled ? 2 : 1));
              } else {
                // update step and reached step to the first step
                dispatch(onUpdateStep(1));
                dispatch(onUpdateReachedStep(1));
                dispatch(onUpdateSubStep(1));
              }
              dispatch(onUpdateSecuritiesData({ holdersData: [] })); // reset holders data
            }}
            secondaryButtonText={
              secondaryButtons[language].unfinishedOnboarding
            }
            onSeccondaryButtonClick={async () => {
              try {
                state?.securities.draftUUID &&
                  (await deleteIndividualDraft({
                    draftUUID: state?.securities.draftUUID,
                  }));

                const personalAccountData: SecuritiesFormikValues["prevStep"] =
                  {
                    email: formik?.values?.email,
                    taxId: formik?.values?.taxId,
                    socialSecurityNumber: formik?.values?.socialSecurityNumber,
                    referred: formik?.values?.referred,
                  };

                if (formik) {
                  formik.resetForm({
                    values: personalAccountData,
                  });
                }

                dispatch(onUpdateStep(1));
                dispatch(onUpdateSubStep(1));
                dispatch(onUpdateReachedStep(1));

                dispatch(
                  onUpdateSecuritiesData({
                    beneficiariesCount: 2,
                    accountBeneficiaries: true,
                    holdersData: [],
                    formValues: null,
                    sendedSteps: [],
                    completedForm: false,
                  })
                );
              } catch (error) {
                console.error(error);
                Sentry.captureException(error);
              }
            }}
          />
        );
      },
    },
  ];

  return {
    step: "0",
    name: "",
    hideStepper: true,
    hideLayoutButtons: true,
    hideFromStepper: true,
    subSteps:
      currentHolder === 1
        ? mainHolderSteps
        : ([
            {
              step: 1,
              name: "Titulares adicionales",
              hideLayoutButtons: true,
              component: (dispatch) => {
                return (
                  <FeedbackCard
                    title={substepTitles[language].additionalHolder}
                    type="notice"
                    icon={addUserIcon}
                    buttonText={primaryButtons[language].additionalHolder}
                    descriptionIsNode
                    description={
                      <div>
                        <p className="text-center">
                          {descriptions[language].additionalHolder}
                        </p>
                      </div>
                    }
                    onButtonClick={() => {
                      dispatch(onUpdateSubStep(2));
                      dispatch(onUpdateLastCompletedSubStep(1));
                    }}
                  />
                );
              },
            },
            {
              ...mainHolderSteps.find((step) => step.step === 2),
              step: 2,
            },

            {
              ...mainHolderSteps.find((step) => step.step === 4),
              step: 3,
            },
          ] as Step[]),
  };
};

export default PrevStep;
